import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, Button, Image, notification } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import config from "../../../../../config/config";
import Imageurldata from "../../../../../Imageurldata";

const EditCategory = ({
  isVisible,
  handleClose,
  dbData,
  moduleName,
  base_url,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const joiOptions = config.joiOptions;

  /* ===================== VALIDATION ===================== */
  const validate = (values) => {
    const schema = Joi.object({
      categoryTitle: Joi.string().min(1).required().label("Category Title"),
    });

    const { error } = schema.validate(values, joiOptions);
    const errors = {};

    if (error) {
      error.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });
    }

    return errors;
  };

  /* ===================== SUBMIT ===================== */
  const handleSubmit = async (values) => {
    const errors = validate(values);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("id", dbData._id);
      formData.append("categoryTitle", values.categoryTitle);
      if (imageFile) {
        formData.append("categoryImageUrl", imageFile);
      }

      const { data } = await axios.put(
        `${base_url}/recipe/category/update`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      notification.success({
        message: `${moduleName} updated successfully`,
      });

      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
      handleClose();

      return data; // return updated data if needed
    } catch (err) {
      notification.error({
        message: "Update failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===================== IMAGE HANDLER ===================== */
  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    return false; // prevent auto upload
  };

  /* ===================== INITIALIZE FORM ===================== */
  useEffect(() => {
    if (dbData) {
      form.setFieldsValue({
        categoryTitle: dbData.categoryTitle || "",
      });

      setImagePreview(
        dbData.categoryImageUrl
          ? Imageurldata + dbData.categoryImageUrl.replaceAll("\\", "/")
          : null
      );
    }
  }, [dbData]);

  return (
    <Modal
      title={`Edit ${moduleName}`}
      visible={isVisible}
      onCancel={handleClose}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
      destroyOnClose
    >
      <Form layout="vertical" form={form}>
        {/* CATEGORY TITLE */}
        <Form.Item
          label="Category Title"
          name="categoryTitle"
          extra="Category title must be unique"
          {...(formError.categoryTitle && {
            help: formError.categoryTitle,
            validateStatus: "error",
          })}
        >
          <Input placeholder="Enter category title" />
        </Form.Item>

        {/* IMAGE UPLOAD */}
        <Form.Item
          label="Category Image"
          extra="Recommended size: 669 x 661 px"
        >
          <Upload
            beforeUpload={handleImageChange}
            maxCount={1}
            accept="image/*"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Select New Image</Button>
          </Upload>

          {/* PREVIEW */}
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <Image
                src={imagePreview}
                width={120}
                height={120}
                style={{ objectFit: "cover", borderRadius: 6 }}
                alt="Category Preview"
                fallback="/no-image.png"
              />
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCategory;
